import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { 
    GetAllPerformanceReviews,
    GetPerformanceReviewById,
    GetMyPerformanceReviews,
    GetManagerPerformanceReviews,
    CreatePerformanceReview,
    UpdatePerformanceReview,
    DeletePerformanceReview,
    GetAllReviewCycles,
    GetReviewCycleById,
    CreateReviewCycle,
    UpdateReviewCycle,
    DeleteReviewCycle,
    GetAllReviewTemplates,
    GetReviewTemplateById,
    CreateReviewTemplate,
    UpdateReviewTemplate,
    DeleteReviewTemplate,
    SubmitSelfReview,
    SubmitManagerReview,
    SubmitHRReview,
    AcknowledgeReview,
    GetAdminPerformanceReviews,
    RequestReviewRevision,
    GetReviewHistory,
    GetReviewStatistics
} from "helpers/api_method";
import { 
    PerformanceReview, 
    ReviewCycle, 
    ReviewTemplate,
    CreateReviewCycleRequest,
    CreateReviewTemplateRequest,
    SubmitSelfReviewRequest,
    SubmitManagerReviewRequest,
    SubmitHRReviewRequest,
    RequestRevisionRequest
} from './types';

// Performance Reviews
export const getAllPerformanceReviews = createAsyncThunk("performanceReview/getAllReviews", async () => {
    try {
        const response = await GetAllPerformanceReviews();
        return response.data || [];
    } catch (error) {
        console.error("Error fetching all performance reviews:", error);
        return [];
    }
});

export const getPerformanceReviewById = createAsyncThunk("performanceReview/getReviewById", async (reviewId: string) => {
    try {
        const response = await GetPerformanceReviewById(reviewId);
        return response.data || null;
    } catch (error) {
        console.error("Error fetching performance review by ID:", error);
        return null;
    }
});

export const getMyPerformanceReviews = createAsyncThunk("performanceReview/getMyReviews", async () => {
    try {
        const response = await GetMyPerformanceReviews();
        return response.data || [];
    } catch (error) {
        console.error("Error fetching my performance reviews:", error);
        return [];
    }
});

export const getManagerPerformanceReviews = createAsyncThunk("performanceReview/getManagerReviews", async () => {
    try {
        const response = await GetManagerPerformanceReviews();
        return response.data || [];
    } catch (error) {
        console.error("Error fetching manager performance reviews:", error);
        return [];
    }
});

// Review Cycles
export const getAllReviewCycles = createAsyncThunk("performanceReview/getAllCycles", async () => {
    try {
        const response = await GetAllReviewCycles();
        return response.data || [];
    } catch (error) {
        console.error("Error fetching review cycles:", error);
        return [];
    }
});

export const createReviewCycle = createAsyncThunk("performanceReview/createCycle", async (cycleData: CreateReviewCycleRequest) => {
    try {
        const response = await CreateReviewCycle(cycleData);
        toast.success("Review cycle created successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Failed to create review cycle", { autoClose: 2000 });
        return error;
    }
});

export const updateReviewCycle = createAsyncThunk("performanceReview/updateCycle", async ({ id, cycleData }: { id: string; cycleData: Partial<CreateReviewCycleRequest> }) => {
    try {
        const response = await UpdateReviewCycle(id, cycleData);
        toast.success("Review cycle updated successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Failed to update review cycle", { autoClose: 2000 });
        return error;
    }
});

// Review Templates
export const getAllReviewTemplates = createAsyncThunk("performanceReview/getAllTemplates", async () => {
    try {
        const response = await GetAllReviewTemplates();
        return response.data || [];
    } catch (error) {
        console.error("Error fetching review templates:", error);
        return [];
    }
});

export const createReviewTemplate = createAsyncThunk("performanceReview/createTemplate", async (templateData: CreateReviewTemplateRequest) => {
    try {
        const response = await CreateReviewTemplate(templateData);
        toast.success("Review template created successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Failed to create review template", { autoClose: 2000 });
        return error;
    }
});

export const updateReviewTemplate = createAsyncThunk("performanceReview/updateTemplate", async ({ id, templateData }: { id: string; templateData: Partial<CreateReviewTemplateRequest> }) => {
    try {
        const response = await UpdateReviewTemplate(id, templateData);
        toast.success("Review template updated successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Failed to update review template", { autoClose: 2000 });
        return error;
    }
});

// Review Submissions
export const submitSelfReview = createAsyncThunk("performanceReview/submitSelfReview", async (reviewData: SubmitSelfReviewRequest) => {
    try {
        const response = await SubmitSelfReview(reviewData.reviewId, reviewData);
        toast.success("Self review submitted successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Failed to submit self review", { autoClose: 2000 });
        return error;
    }
});

export const submitManagerReview = createAsyncThunk("performanceReview/submitManagerReview", async (reviewData: SubmitManagerReviewRequest) => {
    try {
        const response = await SubmitManagerReview(reviewData.reviewId, reviewData);
        toast.success("Manager review submitted successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Failed to submit manager review", { autoClose: 2000 });
        return error;
    }
});

export const submitHRReview = createAsyncThunk("performanceReview/submitHRReview", async (reviewData: SubmitHRReviewRequest) => {
    try {
        const response = await SubmitHRReview(reviewData.reviewId, reviewData);
        toast.success("HR review submitted successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Failed to submit HR review", { autoClose: 2000 });
        return error;
    }
});

export const acknowledgeReview = createAsyncThunk("performanceReview/acknowledgeReview", async (reviewId: string) => {
    try {
        const response = await AcknowledgeReview(reviewId);
        toast.success("Review acknowledged successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Failed to acknowledge review", { autoClose: 2000 });
        return error;
    }
});

// Admin Reviews
export const getAdminPerformanceReviews = createAsyncThunk("performanceReview/getAdminReviews", async () => {
    try {
        const response = await GetAdminPerformanceReviews();
        return response.data || [];
    } catch (error) {
        console.error("Error fetching admin performance reviews:", error);
        return [];
    }
});

export const requestReviewRevision = createAsyncThunk("performanceReview/requestRevision", async (revisionData: RequestRevisionRequest) => {
    try {
        const response = await RequestReviewRevision(revisionData.reviewId, revisionData);
        toast.success("Revision requested successfully", { autoClose: 2000 });
        return response.data;
    } catch (error) {
        toast.error("Failed to request revision", { autoClose: 2000 });
        return error;
    }
});

// Review History
export const getReviewHistory = createAsyncThunk("performanceReview/getHistory", async (filters?: any) => {
    try {
        const response = await GetReviewHistory(filters);
        return response.data || [];
    } catch (error) {
        console.error("Error fetching review history:", error);
        return [];
    }
});

// Review Statistics
export const getReviewStatistics = createAsyncThunk("performanceReview/getStatistics", async () => {
    try {
        const response = await GetReviewStatistics();
        return response.data || null;
    } catch (error) {
        console.error("Error fetching review statistics:", error);
        return null;
    }
});
